// ============================================
// PII Redaction Module (v1 — regex-based)
//
// Strips personally identifiable information before text hits
// external AI providers (Claude, Gemini). Rehydrates on return.
//
// Scope: names, emails, phones, SSNs, DOBs, street addresses.
// Explicitly DOES NOT redact: biomarker names, supplement names,
// medical terms, clinical language, or diagnostic values.
//
// K=TOP: patient data never leaves our perimeter unmasked.
// Full NER replaces this in a future phase.
// ============================================

export interface RedactionResult {
  redacted: string;
  map: Map<string, string>;
}

// --- Medical / Clinical Allowlist ---
// These terms should never be treated as PII "names" even if they
// match Title Case two-word patterns.

const CLINICAL_ALLOWLIST = new Set([
  // Biomarkers & labs
  'Total Testosterone', 'Free Testosterone', 'Estradiol Sensitive',
  'Thyroid Panel', 'Complete Blood', 'Blood Count', 'Metabolic Panel',
  'Hemoglobin A1c', 'Vitamin D', 'Vitamin B12', 'Vitamin B6',
  'Vitamin K', 'Folic Acid', 'Uric Acid', 'Red Blood', 'White Blood',
  'Blood Cells', 'Iron Panel', 'Lipid Panel', 'Liver Panel',
  'Kidney Panel', 'Prostate Specific', 'Insulin Like', 'Growth Factor',
  'Luteinizing Hormone', 'Follicle Stimulating', 'Sex Hormone',
  'Binding Globulin', 'C Reactive', 'Reactive Protein',
  'Dihydrotestosterone DHT', 'Dehydroepiandrosterone DHEA',
  'Thyroid Stimulating', 'Free T3', 'Free T4', 'Total T3', 'Total T4',
  'Blood Pressure', 'Heart Rate', 'Body Mass', 'Body Weight',
  'Lean Mass', 'Fat Mass', 'Bone Density', 'Waist Circumference',
  'Blood Sugar', 'Blood Glucose', 'Fasting Glucose',

  // Supplements & compounds
  'Ashwagandha Extract', 'Tongkat Ali', 'Fadogia Agrestis',
  'Saw Palmetto', 'Stinging Nettle', 'Milk Thistle', 'Fish Oil',
  'Omega Fatty', 'Zinc Picolinate', 'Magnesium Glycinate',
  'Boron Citrate', 'Vitamin Complex', 'Amino Acid', 'Amino Acids',
  'Coenzyme Q10',

  // Clinical terms
  'Lab Analyst', 'Protocol Builder', 'Intake Nurse',
  'Medical Director', 'Clinical Review', 'Health Summary',
  'Treatment Plan', 'Care Plan', 'Follow Up', 'Side Effects',
  'Adverse Event', 'Adverse Events', 'Dose Adjustment',
  'Blood Draw', 'Lab Work', 'Lab Results', 'Test Results',
  'Optimal Range', 'Reference Range', 'Normal Range',
  'Gate Logic', 'Gate Check', 'Promise Keeper',
  'Flight Attendant', 'Open Loop', 'Fashion Plate',

  // Conditions & symptoms
  'Low Testosterone', 'High Estrogen', 'Erectile Dysfunction',
  'Weight Loss', 'Weight Management', 'Hair Loss', 'Brain Fog',
  'Sleep Quality', 'Joint Pain', 'Muscle Recovery',
  'Chronic Fatigue', 'Metabolic Syndrome',

  // Common two-word phrases that aren't names
  'Last Updated', 'Next Steps', 'Per Day', 'Per Week',
  'Once Daily', 'Twice Daily', 'As Needed', 'Not Available',
  'No Data', 'See Below', 'See Above',
]);

// Single-word terms that should never be redacted even if adjacent to
// another capitalized word
const CLINICAL_SINGLE_WORDS = new Set([
  'testosterone', 'estradiol', 'progesterone', 'cortisol', 'insulin',
  'hemoglobin', 'hematocrit', 'ferritin', 'creatinine', 'albumin',
  'bilirubin', 'triglycerides', 'cholesterol', 'glucose', 'potassium',
  'sodium', 'calcium', 'magnesium', 'zinc', 'iron', 'copper', 'selenium',
  'iodine', 'boron', 'dhea', 'shbg', 'psa', 'igf', 'tsh', 'lh', 'fsh',
  'prolactin', 'melatonin', 'serotonin', 'dopamine', 'oxytocin',
  'sermorelin', 'enclomiphene', 'anastrozole', 'tamoxifen', 'clomiphene',
  'tadalafil', 'sildenafil', 'semaglutide', 'tirzepatide',
  'biomarker', 'biomarkers', 'protocol', 'formulary', 'dosage',
  'mg', 'ml', 'mcg', 'iu', 'mmol', 'nmol', 'pg', 'ng', 'dl',
]);

function isClinicalTerm(text: string): boolean {
  if (CLINICAL_ALLOWLIST.has(text)) return true;

  // Check if either word is a known clinical single word
  const words = text.toLowerCase().split(/\s+/);
  return words.some((w) => CLINICAL_SINGLE_WORDS.has(w));
}

// --- Regex Patterns ---

// SSN: XXX-XX-XXXX (must come before phone to avoid overlap)
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;

// Email
const EMAIL_RE = /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g;

// Phone: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX.XXX.XXXX, +1XXXXXXXXXX, etc.
const PHONE_RE = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;

// Date of birth: MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD, YYYY/MM/DD
// Only matches plausible dates (month 01-12, day 01-31, year 1900-2099)
const DOB_RE = /\b(?:(?:0[1-9]|1[0-2])[\/\-](?:0[1-9]|[12]\d|3[01])[\/\-](?:19|20)\d{2}|(?:19|20)\d{2}[\/\-](?:0[1-9]|1[0-2])[\/\-](?:0[1-9]|[12]\d|3[01]))\b/g;

// Street address: number + street name (e.g., "123 Main Street", "4567 Oak Ave Apt 2B")
// Anchored to common street suffixes to avoid false positives
const STREET_SUFFIXES = '(?:Street|St|Avenue|Ave|Boulevard|Blvd|Drive|Dr|Road|Rd|Lane|Ln|Court|Ct|Place|Pl|Way|Circle|Cir|Trail|Trl|Parkway|Pkwy|Highway|Hwy|Suite|Ste|Apt|Unit|Floor|Fl)';
const ADDR_RE = new RegExp(
  `\\b\\d{1,6}\\s+[A-Z][a-z]+(?:\\s+[A-Z][a-z]+)*\\s+${STREET_SUFFIXES}\\b\\.?(?:\\s+(?:Apt|Suite|Ste|Unit|#)\\s*[A-Za-z0-9]+)?`,
  'g',
);

// Full names: Title Case two-word patterns (First Last)
// Excludes lines that start with common non-name patterns
// Applied LAST after all other PII types are already tokenized
const NAME_RE = /\b([A-Z][a-z]{1,20})\s+([A-Z][a-z]{1,20})\b/g;


// --- Core Functions ---

/**
 * Redact PII from text. Returns the scrubbed text and a map of
 * token -> original value for rehydration.
 *
 * Each call gets its own counter state so concurrent calls
 * never share or leak PII across requests.
 */
export function redact(text: string): RedactionResult {
  const map = new Map<string, string>();

  // Reverse lookup: original value -> token (for deduplication)
  const seen = new Map<string, string>();

  // Per-type counters
  const counters: Record<string, number> = {
    SSN: 0,
    EMAIL: 0,
    PHONE: 0,
    DOB: 0,
    ADDR: 0,
    PATIENT: 0,
  };

  function getToken(type: string, original: string): string {
    // If we already assigned a token to this exact value, reuse it
    if (seen.has(original)) {
      return seen.get(original)!;
    }
    counters[type]++;
    const token = `[${type}_${counters[type]}]`;
    map.set(token, original);
    seen.set(original, token);
    return token;
  }

  let result = text;

  // Order matters: SSN before phone (SSN is more specific),
  // addresses before names (addresses contain name-like words)

  // 1. SSN
  result = result.replace(SSN_RE, (match) => getToken('SSN', match));

  // 2. Email
  result = result.replace(EMAIL_RE, (match) => getToken('EMAIL', match));

  // 3. Phone
  result = result.replace(PHONE_RE, (match) => getToken('PHONE', match));

  // 4. DOB
  result = result.replace(DOB_RE, (match) => getToken('DOB', match));

  // 5. Addresses (before names — addresses contain capitalized words)
  result = result.replace(ADDR_RE, (match) => getToken('ADDR', match));

  // 6. Names (last — after other patterns have been replaced)
  result = result.replace(NAME_RE, (fullMatch, first, last) => {
    const candidate = `${first} ${last}`;

    // Skip if it's a known clinical/medical term
    if (isClinicalTerm(candidate)) return fullMatch;

    return getToken('PATIENT', candidate);
  });

  return { redacted: result, map };
}

/**
 * Merge multiple redaction maps into a single combined map.
 * Used when redacting multiple strings (system prompt, history,
 * user message) that all need to share one rehydration pass.
 */
export function mergeRedactionMaps(...maps: Map<string, string>[]): Map<string, string> {
  const merged = new Map<string, string>();
  for (const m of maps) {
    for (const [token, original] of m) {
      merged.set(token, original);
    }
  }
  return merged;
}

/**
 * Re-attach original PII values into AI response text.
 * Replaces every token found in the map with its original value.
 */
export function rehydrate(text: string, map: Map<string, string>): string {
  let result = text;
  for (const [token, original] of map) {
    // Replace all occurrences of the token (the AI might repeat them)
    result = result.split(token).join(original);
  }
  return result;
}

/**
 * Convenience: redact an array of RouterMessages in place,
 * returning a combined map for later rehydration.
 */
export function redactMessages<T extends { content: string }>(
  messages: T[],
): { redactedMessages: T[]; map: Map<string, string> } {
  const maps: Map<string, string>[] = [];
  const redactedMessages = messages.map((m) => {
    const { redacted, map } = redact(m.content);
    maps.push(map);
    return { ...m, content: redacted };
  });
  return { redactedMessages, map: mergeRedactionMaps(...maps) };
}
